/**
 * El chat del sandbox: el MISMO hilo que las bandejas reales, dentro del MISMO dock.
 *
 * 🚨 ANTES ERA UN FORMULARIO DISFRAZADO. Un banner, una tarjeta con las respuestas en texto plano y
 * un `textarea` con dos botones. Y no es solo estético: probar al agente es leer si suena bien en
 * WhatsApp, y en texto plano no se ve el largo real de la burbuja, ni la negrita, ni cómo queda
 * partido en dos mensajes. Se evaluaba algo que el cliente nunca ve así.
 *
 * 🪤 SE REUTILIZA `WaMessageThread`, EL HILO DE VERDAD — no una copia parecida. Con eso vienen la
 * misma burbuja, el mismo fondo, el mismo tokenizador de negritas y el mismo chip de origen. Una
 * versión "igualita" escrita al lado empieza idéntica y a la tercera edición ya miente.
 *
 * 🪤 PERO EL COMPOSITOR NO SE REUTILIZA, Y ES A PROPÓSITO. `WaChatComposer` está atado a una
 * conversación real: pide tenant, id de conversación, respuestas rápidas, subida de audio y archivos,
 * aviso de "escribiendo". Forzarlo acá obligaría a inventar una conversación falsa y a cablear media
 * que el sandbox no manda. Va uno delgado que se ve igual y solo escribe texto.
 *
 * 🪤 Y EL COMPONENTE SE MANEJA SOLO. Su estado vive acá y no en la página del Studio porque la ventana
 * la administra el dock, que sobrevive a la navegación: así se puede seguir probando mientras se
 * mira otra pantalla. Si el estado viviera en la página, cambiar de ruta borraría la conversación.
 */
import { useCallback, useMemo, useRef, useState } from 'react';
import { Text } from '@/lib/polaris';
import { WaChatComposer } from '@/components/whatsapp/WaChatComposer';
import { WaMessageThread, type WaMessage } from '@/components/whatsapp/WaMessageThread';
import { waChatBgStyle } from '@/components/whatsapp/wa-chat-bg';
import { supabase } from '@/lib/supabase';

interface MensajeDePrueba {
  role: 'user' | 'assistant';
  content: string;
  /** Data URI de la imagen adjunta, si la hay. Se manda al agente y se pinta en la burbuja. */
  imagen?: string;
  /**
   * Qué herramientas consultó el agente para armar esta respuesta.
   *
   * 🪤 EL SANDBOX YA LAS DEVOLVÍA Y NADIE LAS MOSTRABA. Sin esto, un precio raro es indistinguible
   * entre tres causas muy distintas: no consultó el catálogo, lo consultó y lo ignoró, o el catálogo
   * está mal cargado. Se depura a ciegas — y la espera de los puntitos tampoco se entiende.
   */
  herramientas?: string[];
  /** Issues por los que el validador de producción habría frenado esta respuesta. */
  bloqueado?: string[];
}

/**
 * Convierte la conversación del sandbox en mensajes del hilo real.
 *
 * 🪤 ACÁ LOS LADOS VAN AL REVÉS QUE EN LA BANDEJA, Y ES CORRECTO. En un chat de cliente, el verde a la
 * derecha es «lo que mandó el negocio». En el sandbox el que escribe sos VOS: tus mensajes van verdes
 * a la derecha y los del agente en blanco a la izquierda, porque el agente te está respondiendo a vos.
 * Al revés, lo que acabás de tipear aparecería como si lo hubiera dicho otro — y eso desorienta.
 *
 * 🪤 EL PRECIO DE ESTA DECISIÓN, DICHO EN VOZ ALTA: en la bandeja real la conversación se va a ver
 * espejada, con el agente del lado verde. Lo que se gana es que probar se sienta como chatear; lo que
 * se pierde es la vista previa exacta de cómo lo verá el operador.
 */
/**
 * La línea que cuenta qué hizo el agente para esa respuesta. Se pinta como NOTA INTERNA, que el hilo
 * ya sabe mostrar distinto y el cliente nunca vería.
 *
 * 🪤 SIN ESTO SE DEPURA A CIEGAS. Un precio raro tiene tres causas muy distintas —no consultó el
 * catálogo, lo consultó y lo ignoró, o el catálogo está mal cargado— y desde afuera se ven idénticas.
 * Medido el 12-ago-2026: contestó «S/99» teniendo los precios reales a mano, y no había forma de
 * saber cuál de las tres era sin abrir los logs. De paso explica la espera: los puntitos duran más
 * porque ahora consulta de verdad.
 */
function notaDeDiagnostico(m: MensajeDePrueba): string | null {
  const partes: string[] = [];
  if (m.herramientas?.length) partes.push(`consultó: ${m.herramientas.join(', ')}`);
  else if (m.role === 'assistant') partes.push('no consultó ninguna herramienta');
  if (m.bloqueado?.length) partes.push(`⚠️ en producción esto se frenaría: ${m.bloqueado.join(', ')}`);
  return partes.length ? partes.join(' · ') : null;
}

function aMensajesDelHilo(mensajes: MensajeDePrueba[]): WaMessage[] {
  const filas = mensajes.map((m, i) => ({
    id: `prueba-${i}`,
    direction: m.role === 'user' ? 'outbound' : 'inbound',
    sender_type: m.role === 'user' ? 'agent' : 'contact',
    sender_id: null,
    body: m.content,
    msg_type: m.imagen ? 'image' : 'text',
    media_url: m.imagen ?? null,
    media_caption: m.imagen ? m.content : null,
    media_filename: null,
    media_status: null,
    delivery_status: 'read',
    is_internal_note: false,
    created_at: new Date().toISOString(),
    reactions: [],
    wa_message_id: null,
    // El chip «IA» va en el mensaje del agente, que ahora es el entrante.
    origen: m.role === 'assistant' ? 'agente_ia' : null,
  }));

  // La nota va DESPUÉS de su respuesta, como el apunte de alguien que revisa el turno.
  return filas.flatMap((fila, i) => {
    const nota = notaDeDiagnostico(mensajes[i]);
    if (!nota) return [fila];
    return [fila, { ...fila, id: `${fila.id}-nota`, body: nota, is_internal_note: true, origen: null }];
  });
}

export function ChatDePrueba({
  tenantId,
  agenteId,
  canal,
}: {
  tenantId: string | null;
  agenteId: string;
  /** Por dónde se está probando: cambia el FORMATO de la respuesta, no la persona. */
  canal: string;
}) {
  const [mensajes, setMensajes] = useState<MensajeDePrueba[]>([]);
  const [texto, setTexto] = useState('');
  /*
   * De qué vienen hablando, tal como lo devolvió la última respuesta.
   *
   * 🪤 EL ESTADO VIVE ACÁ Y NO EN EL SERVIDOR PORQUE EL SANDBOX NO GUARDA NADA — esa es su promesa.
   * En producción lo mismo se anota en `wa_conversations.agent_slots`; acá viaja con el pedido.
   */
  const [catalogoEnJuego, setCatalogoEnJuego] = useState('');
  const [enviando, setEnviando] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const finRef = useRef<HTMLDivElement>(null);


  /** Lee el archivo como data URI y lo manda: sin subirlo a ningún lado. */
  const adjuntar = useCallback((archivo: File | null | undefined) => {
    if (!archivo) return;
    // 🪤 Tope de tamaño: un data URI enorme infla el pedido y el modelo lo rechaza igual.
    if (archivo.size > 4 * 1024 * 1024) { setError('La imagen pesa más de 4 MB.'); return; }
    const lector = new FileReader();
    lector.onload = () => { void enviarRef.current?.(String(lector.result)); };
    lector.onerror = () => setError('No se pudo leer la imagen.');
    lector.readAsDataURL(archivo);
  }, []);

  const delHilo = useMemo(() => aMensajesDelHilo(mensajes), [mensajes]);

  const enviar = useCallback(async (imagen?: string, textoExterno?: string) => {
    // El compositor real maneja su propio borrador: cuando envía, lo pasa acá.
    const contenido = (textoExterno ?? texto).trim();
    // Con una imagen adjunta el texto puede ir vacío: la foto ES el mensaje.
    if ((!contenido && !imagen) || enviando || !tenantId) return;
    setError(null);
    const siguiente: MensajeDePrueba[] = [...mensajes, { role: 'user', content: contenido, imagen }];
    setMensajes(siguiente);
    setTexto('');
    setEnviando(true);
    try {
      const { data: sess } = await supabase.auth.getSession();
      const token = sess?.session?.access_token ?? null;
      if (!token) { setError('No hay sesión activa.'); return; }

      const base = (import.meta.env.VITE_SUPABASE_URL as string).replace(/\/$/, '');
      const res = await fetch(`${base}/functions/v1/agent-test-chat`, {
        method: 'POST',
        headers: {
          Authorization: `Bearer ${token}`,
          'Content-Type': 'application/json',
          apikey: import.meta.env.VITE_SUPABASE_ANON_KEY as string,
        },
        body: JSON.stringify({
          tenant_id: tenantId,
          agent_id: agenteId,
          /*
           * 🪤 LA IMAGEN VA EN LA MISMA FORMA QUE PRODUCCIÓN: una lista de partes con
           * `{ type: 'image', url }`. Si el sandbox inventara la suya, probaría un camino que el
           * agente real no recorre. Y la url es un data URI — la capa de LLM lo acepta embebido, así
           * que no hay que subir el archivo a ningún lado ni decidir quién lo borra después.
           */
          messages: siguiente.map((m) => (m.imagen
            ? {
                role: m.role,
                content: [
                  ...(m.content ? [{ type: 'text', text: m.content }] : []),
                  { type: 'image', url: m.imagen, mediaType: 'image/jpeg' },
                ],
              }
            : { role: m.role, content: m.content })),
          // El canal decide CÓMO escribe: en la web los asteriscos de WhatsApp se ven literales.
          canal,
          // Lo consultado en los turnos anteriores: sin esto el agente pierde de qué se hablaba.
          catalogo_en_juego: catalogoEnJuego,
        }),
      });
      const json = (await res.json()) as {
        reply?: string; error?: string;
        tool_calls?: { name: string }[];
        bloqueado_en_produccion?: string[];
        catalogo_en_juego?: string;
      };
      if (!res.ok || json.error) { setError(json.error ?? `Error ${res.status}`); return; }
      if (typeof json.catalogo_en_juego === 'string') setCatalogoEnJuego(json.catalogo_en_juego);
      if (json.reply) {
        setMensajes((prev) => [...prev, {
          role: 'assistant',
          content: json.reply as string,
          // Se deduplican conservando el orden: ver dos veces "search_products" no agrega nada.
          herramientas: [...new Set((json.tool_calls ?? []).map((t) => t.name))],
          bloqueado: json.bloqueado_en_produccion,
        }]);
      }
    } catch (e) {
      setError(`Error de red: ${String(e)}`);
    } finally {
      setEnviando(false);
    }
  }, [texto, enviando, tenantId, agenteId, canal, mensajes, catalogoEnJuego]);

  /*
   * 🪤 UNA REFERENCIA Y NO LA FUNCIÓN DIRECTA. `adjuntar` la llama desde el callback del lector de
   * archivos, que se resuelve después: capturarla por valor congelaría los mensajes de ese instante y
   * la foto se mandaría con una conversación vieja.
   */
  const enviarRef = useRef(enviar);
  enviarRef.current = enviar;

  return (
    /*
     * 🪤 EL FONDO VA EN LA RAÍZ, NO EN EL ÁREA DE MENSAJES. Lo tenía al revés y por eso el
     * compositor quedaba sobre un blanco opaco en vez de sobre el patrón: el chat real pinta el
     * fondo en el contenedor y deja que TODO lo de adentro —incluido el teclado— se apoye encima.
     * Es la diferencia que se ve poniendo las dos ventanas lado a lado.
     */
    <div style={{
      ...waChatBgStyle,
      display: 'flex', flexDirection: 'column', flex: 1, minHeight: 0, overflow: 'hidden',
    }}>
      <div style={{ flex: 1, overflowY: 'auto', padding: 12, minHeight: 0 }}>
        {mensajes.length === 0 ? (
          <div style={{ padding: '26px 20px', textAlign: 'center' }}>
            <Text as="p" variant="bodySm" tone="subdued">
              Escribí como si fueras un cliente. El agente responde con su configuración actual —
              no se envía ningún mensaje real.
            </Text>
          </div>
        ) : (
          <WaMessageThread
            messages={delHilo}
            loading={false}
            /* En un sandbox no hay a quién reaccionarle: el hilo lo pide, acá no hace nada. */
            onSendReaction={() => {}}
            messagesEndRef={finRef}
          />
        )}
        {/*
          * 🪤 TRES PUNTITOS EN UNA BURBUJA, NO UN SPINNER. Un spinner dice «el sistema está
          * procesando»; los puntos dicen «la otra persona está escribiendo», que es lo que pasa. Y
          * como van en una burbuja del lado del agente, ocupan el lugar donde va a aparecer la
          * respuesta — el hilo no salta cuando llega.
          */}
        {enviando && (
          <div style={{ display: 'flex', padding: '2px 16px 10px' }}>
            <div
              aria-label="escribiendo"
              style={{
                display: 'flex', gap: 4, alignItems: 'center',
                background: '#fff', padding: '10px 14px',
                borderRadius: '0 8px 8px 8px', boxShadow: '0 1px .5px rgba(0,0,0,.13)',
              }}
            >
              {[0, 1, 2].map((i) => (
                <span
                  key={i}
                  style={{
                    width: 7, height: 7, borderRadius: '50%', background: '#9aa3a8',
                    display: 'block',
                    animation: 'wa-escribiendo 1.2s infinite ease-in-out',
                    animationDelay: `${i * 0.18}s`,
                  }}
                />
              ))}
            </div>
          </div>
        )}
        <style>{`
          @keyframes wa-escribiendo {
            0%, 60%, 100% { opacity: .35; transform: translateY(0); }
            30% { opacity: 1; transform: translateY(-3px); }
          }
          @media (prefers-reduced-motion: reduce) {
            [aria-label="escribiendo"] span { animation: none !important; opacity: .6 !important; }
          }
        `}</style>
      </div>

      {error ? (
        <div style={{ padding: '7px 12px', background: 'var(--p-color-bg-surface-critical)', flexShrink: 0 }}>
          <Text as="span" variant="bodySm" tone="critical">{error}</Text>
        </div>
      ) : null}

      {/*
        * 🪤 EL COMPOSITOR ES EL MISMO DE LA BANDEJA, no uno parecido. Lo pedido fue explícito:
        * «agregale el mismo teclado que tienen los chats». Reutilizarlo trae el «+» con su panel, los
        * stickers, el emoji y la grabación exactamente como en un chat real — y cualquier mejora que
        * se le haga a ese compositor llega acá sola.
        *
        * 🪤 LO QUE NO APLICA SE APAGA PASANDO NADA, no reimplementando. `conversationId` en null le
        * dice que no hay conversación real: las respuestas rápidas van vacías y no hay a quién
        * avisarle que estás escribiendo. Lo que sí aplica —texto e imagen— se conecta abajo.
        */}
      <WaChatComposer
        tenantId={tenantId}
        conversationId={null}
        conversationStatus={null}
        quickReplies={[]}
        lastInboundMessage={null}
        onSend={(t) => { void enviarRef.current?.(undefined, t); }}
        onSendMedia={(file, tipo) => {
          // Solo imagen: el endpoint del sandbox recibe imágenes embebidas. Ver `adjuntar`.
          if (tipo !== 'image') { setError('Por ahora la prueba solo acepta imágenes.'); return; }
          adjuntar(file);
        }}
      />

    </div>
  );
}
